import type { ReactElement, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type KpiVariant = 'default' | 'highlight';
export type KpiDeltaDirection = 'up' | 'down' | 'flat';

export interface KpiCardProps {
  /** Label superior, renderizado em JetBrains Mono uppercase */
  label: string;
  /** Valor principal (já formatado em pt-BR: ex "R$ 12.345,67" ou "4,8x") */
  value: string;
  /** Descrição abaixo do valor */
  description?: string;
  /** Variação % vs período anterior (ex: 12.4 → "+12,4%") */
  deltaPct?: number;
  /** Direção do delta (afeta cor); se omitido, deduz de deltaPct */
  deltaDirection?: KpiDeltaDirection;
  /** Ícone Tabler (já elementizado) */
  icon?: ReactNode;
  /** Variante visual */
  variant?: KpiVariant;
  /** Slot opcional ao final */
  footer?: ReactNode;
  className?: string;
}

function formatDelta(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1).replace('.', ',')}%`;
}

function deduceDirection(pct: number | undefined): KpiDeltaDirection {
  if (pct === undefined || pct === 0) return 'flat';
  return pct > 0 ? 'up' : 'down';
}

export function KpiCard({
  label,
  value,
  description,
  deltaPct,
  deltaDirection,
  icon,
  variant = 'default',
  footer,
  className,
}: KpiCardProps): ReactElement {
  const direction = deltaDirection ?? deduceDirection(deltaPct);
  const deltaColor =
    direction === 'up'
      ? 'text-color-violet'
      : direction === 'down'
        ? 'text-color-magenta'
        : 'text-ink-faint';

  return (
    <article
      className={cn(
        'group relative overflow-hidden rounded-3xl p-6 backdrop-blur-xl transition-all duration-300',
        variant === 'highlight'
          ? 'bg-color-violet/[0.10] ring-color-violet/30 ring-1'
          : 'bg-surface/60 hover:bg-surface',
        className,
      )}
    >
      {/* Gradient blob no canto (sutil) */}
      <span
        aria-hidden
        className="bg-trakd-brand pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full opacity-[0.07] blur-2xl transition-opacity group-hover:opacity-[0.14]"
      />

      <header className="relative flex items-start justify-between gap-3">
        <span className="text-ink-muted font-mono text-[10px] tracking-[0.14em] uppercase">
          {label}
        </span>
        {icon ? (
          <span className="bg-surface-elevated/60 text-color-violet group-hover:bg-surface-elevated flex h-11 w-11 items-center justify-center rounded-2xl transition-colors">
            {icon}
          </span>
        ) : null}
      </header>

      <p className="text-ink relative mt-3 text-3xl leading-none font-semibold tracking-tight tabular-nums">
        {value}
      </p>

      {description ? (
        <p className="text-ink-muted relative mt-1.5 text-xs leading-relaxed">{description}</p>
      ) : null}

      {deltaPct !== undefined ? (
        <p
          className={cn(
            'relative mt-3 font-mono text-[11px] tracking-tight tabular-nums',
            deltaColor,
          )}
        >
          {formatDelta(deltaPct)}
          <span className="text-ink-faint ml-2">vs. anterior</span>
        </p>
      ) : null}

      {footer ? <div className="relative mt-3">{footer}</div> : null}
    </article>
  );
}
